import { useCallback, useEffect, useState } from 'react'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { useLocation, useSearchParams } from 'react-router-dom'

import {
  FETCH_CHECKOUT_SESSION_KEY,
  fetchBillingPortalSession,
  fetchCheckoutSession,
  type StripeProduct,
} from '@/actions/settings/billingActions'
import {
  sendOnboardingZap,
  sendUserCRMCreationZap,
} from '@/actions/zapierActions'
import {
  fetchNeedsTwoCheckouts,
  setCatchUpBookkeepingPaymentRequirements,
} from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.slice'
import { getIsFetching } from '@/reducers/fetch'
import { selectSubscription } from '@/reducers/subscription.slice'
import { getCurrentUser } from '@/selectors/user.selectors'
import { useFreeTrialEnabled } from '../useFreeTrialEnabled'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { selectHasAnnualCommitment } from '@/features/user/settings/sections/self-serve-cancellation/lib/userCancellation.selectors'

import {
  useAnnualCommitmentPrice,
  useCatchupBookkeepingPrice,
} from '../payment/hooks'
import {
  catchUpBkTreatment,
  resolveTwoCheckoutFlow,
  type CatchUpBkTreatment,
} from '../payment/helpers'
import { buildCheckoutParams } from './buildCheckoutParams'
import { CHECKOUT_ERROR_MESSAGE } from './checkoutErrorMessage'
import type { UseSignupCheckoutInput } from './signup-checkout.types'
import type { CheckoutSelection } from '../plans/signupPlans.types'

const toSelection = (
  input: CheckoutSelection | StripeProduct | undefined
): CheckoutSelection | undefined =>
  input && 'default_price' in input
    ? { priceId: input.default_price.id, productId: input.id }
    : input

/**
 * Owns Stripe checkout construction for signup. Fetches on mount, so mounting it is
 * not free.
 */
export const useSignupCheckout = ({
  selection,
  selectedProduct,
  successUrl,
  screenName,
  isUpsell,
  serverWaived,
}: UseSignupCheckoutInput) => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const user = useReselector(getCurrentUser)
  const subscription = useReselector(selectSubscription)
  const loading = useReselector(getIsFetching, FETCH_CHECKOUT_SESSION_KEY)
  const catchupBookkeepingPrice = useCatchupBookkeepingPrice()
  const hasAnnualCommitment = useReselector(selectHasAnnualCommitment)
  const { getAnnualCommitmentPriceId } = useAnnualCommitmentPrice()
  const [needsTwoCheckouts, setNeedsTwoCheckouts] = useState(false)
  const [checkoutFailed, setCheckoutFailed] = useState(false)

  const freeTrialSearchParamValue = searchParams.get('trial')
  const freeTrialEnabled = useFreeTrialEnabled(freeTrialSearchParamValue)
  // Plain hook, not `useLoadedFlagValue`: that one nulls without a targeting key, which
  // would pin the waiver to its default across the pre-account signup steps.
  const cubkWaiverEnabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.waiveCubkForFreeTrial2026,
    false
  )

  // The second checkout exists only to collect catch up bookkeeping, so a user whose
  // fee is waived is not in that flow at all — rather than every caller remembering to
  // pair the flag with a waiver check.
  const useTwoCheckoutFlow = resolveTwoCheckoutFlow({
    needsTwoCheckouts,
    trialCode: freeTrialSearchParamValue,
    freeTrialEnabled,
    waiverEnabled: cubkWaiverEnabled,
  })

  useEffect(() => {
    const checkForMultiDiscounts = async () => {
      const res = await fetchNeedsTwoCheckouts()(dispatch)
      setNeedsTwoCheckouts(Boolean(res))
    }

    if (catchupBookkeepingPrice?.priceId) {
      checkForMultiDiscounts()
    }
  }, [dispatch, catchupBookkeepingPrice?.priceId])

  /**
   * For a caller that picks a plan and charges for it in one click: `submit` would
   * read state from the previous render and charge for the previous selection.
   */
  const submitWith = useCallback(
    async (
      input: CheckoutSelection | StripeProduct | undefined
    ): Promise<boolean> => {
      const selection = toSelection(input)
      if (!user?.id || !selection?.priceId) {
        return false
      }

      const commitmentPriceId =
        hasAnnualCommitment && selection.productId
          ? getAnnualCommitmentPriceId(selection.productId)?.id
          : undefined
      const priceId = commitmentPriceId ?? selection.priceId

      setCheckoutFailed(false)
      await setCatchUpBookkeepingPaymentRequirements()(dispatch)
      let subscriptionCheckoutUrl: string | null = null
      const hasSubscription = Object.keys(subscription).length > 0

      if (!hasSubscription) {
        const res = await fetchCheckoutSession(
          buildCheckoutParams({
            userId: user.id,
            priceId,
            catchUpBookkeeping: catchupBookkeepingPrice,
            freeTrialEnabled,
            freeTrialSearchParamValue,
            useTwoCheckoutFlow,
            isUpsell: Boolean(isUpsell),
            successUrl,
            cancelUrl: `${location.pathname}${location.search}`,
            cubkWaiverEnabled,
            serverWaived,
          })
        )(dispatch)

        if (res) {
          subscriptionCheckoutUrl = res.url
        }
        // This means this is a free trial to paid conversion
      } else {
        const res = await fetchBillingPortalSession(isUpsell)(dispatch)

        if (res) {
          subscriptionCheckoutUrl = res.url
        }
      }

      if (subscriptionCheckoutUrl !== null) {
        window.location.href = subscriptionCheckoutUrl

        // Only relevant for new users, not for upsells, as this was already done on sign up
        if (!isUpsell) {
          sendUserCRMCreationZap({
            createdAt: user.createdAt,
            email: user.email,
            financialProfile: user.financialProfile,
            firstName: user.firstName,
            lastName: user.lastName,
          })

          sendOnboardingZap({
            email: user.email,
            screenName,
          })()
        }

        return true
      }

      setCheckoutFailed(true)
      return false
    },
    [
      cubkWaiverEnabled,
      serverWaived,
      user?.id,
      user?.createdAt,
      user?.email,
      user?.financialProfile,
      user?.firstName,
      user?.lastName,
      hasAnnualCommitment,
      catchupBookkeepingPrice,
      location.pathname,
      location.search,
      freeTrialEnabled,
      freeTrialSearchParamValue,
      dispatch,
      subscription,
      useTwoCheckoutFlow,
      isUpsell,
      successUrl,
      screenName,
      getAnnualCommitmentPriceId,
    ]
  )

  const submit = useCallback(
    () => submitWith(selection ?? selectedProduct),
    [submitWith, selection, selectedProduct]
  )

  // Exposed so a surface that *states* the fee (a due-today total, trial copy)
  // reads the same answer that bills it, rather than re-deriving it from the
  // trial code and flags and drifting.
  const treatment = catchUpBkTreatment({
    trialCode: freeTrialSearchParamValue,
    freeTrialEnabled,
    useTwoCheckoutFlow,
    waiverEnabled: cubkWaiverEnabled,
    serverWaived,
  })

  return {
    submit,
    submitWith,
    loading,
    checkoutError: checkoutFailed ? CHECKOUT_ERROR_MESSAGE : null,
    useTwoCheckoutFlow,
    freeTrialEnabled,
    catchUpBkTreatment: treatment satisfies CatchUpBkTreatment,
  }
}
